import { Component, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, FormBuilder } from "@angular/forms";
import { Subscription } from "rxjs";
import { merge } from "rxjs/internal/observable/merge";
import { ProductCollectionGroupsEnum } from "../../../../shared/enums/product-collection-groups-enum";
import { ProductCollectionsEnum } from "../../../../shared/enums/product-collections.enum";
import { ProductsSearchModeEnum } from "../../../../shared/enums/products-search-mode.enum";
import { DropdownOptionModel } from "../../../../shared/models/components/form-controls/dropdown-option.model";
import { AggregationGroupModel } from "../../../../shared/models/search/aggregations/aggregation-group.model";
import { ProductResultItemModel } from "../../../../shared/models/search/results/items/product-result-item.model";
import { ProductsParamsFilter } from "../../../models/search-params/filters/products-params-filter";
import { ProductsResultsFilter } from "../../../models/search-results/filters/products-results-filter";
import { ScopedSearchResultModel } from "../../../models/search-results/scoped-search-result.model";
import { SearchTabDirective } from "../search-tab.directive";
import { ProductsFilterForm } from "./forms/products-filter.form";
import { ProductsFilterFormService } from "./services/products-filter-form.service";

@Component({
    selector: "pcg-ng-products-tab",
    template: require('./products-tab.component.html')
})

export class ProductsTabComponent extends SearchTabDirective<ProductsParamsFilter, ScopedSearchResultModel<ProductResultItemModel, ProductsResultsFilter>>
    implements OnInit, OnDestroy {

    private submitSubscription: Subscription;

    private searchResultSubscription: Subscription;

    private filterForm: ProductsFilterForm;

    public sortingOptions: Array<DropdownOptionModel> = new Array<DropdownOptionModel>();

    public searchMode: ProductsSearchModeEnum;

    public constructor(
        private formBuilder: FormBuilder,
        private filterFormService: ProductsFilterFormService,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.sortingOptions = this.filterFormService.getSortingOptions();

        this.filterForm = ProductsFilterForm.createForm(this.formBuilder, this.filter, this.filterFormService);
        this.submitSubscription = merge(
                this.filterForm.changeObservable,
                this.filterForm.submitObservable
            )
            .subscribe((filter: ProductsParamsFilter) => this.filterSubmitted.emit(filter));
        this.processSearchResult();

        this.searchResultSubscription = this.searchResultsUpdated
            .subscribe(() => {
                this.processSearchResult();
            });
    }

    private processSearchResult(): void {
        this.setSearchMode();
        if(this.searchResult.pagination.totalItems === 0) {
            this.filter.collectionGroup = ProductCollectionGroupsEnum.All;
            this.filter.collection = ProductCollectionsEnum.All;
        }
        this.filterForm.buildFormControls(this.filter, this.searchResult.filter);
    }

    public ngOnDestroy(): void {
        if (this.submitSubscription) {
            this.submitSubscription.unsubscribe();
            this.submitSubscription = null;
        }
        if (this.searchResultSubscription) {
            this.searchResultSubscription.unsubscribe();
            this.searchResultSubscription = null;
        }
        if(this.filterForm) {
            this.filterForm.dispose();
        }
    }

    public isCollectionsFilterVisible(): boolean {
        const currentFilter = this.filterForm.getModel();
        const currentCollectionGroup = currentFilter.collectionGroup;

        let isVisible: boolean;
        if(currentCollectionGroup === ProductCollectionGroupsEnum.All) {
            isVisible = false;
        } else {
            isVisible = !this.filterFormService.isSingleCollectionGroup(currentFilter.collectionGroup);
        }

        return isVisible;
    }

    public getControlsArray(): Array<AbstractControl> {
        return Object.keys(this.filterForm.facets.controls)
            .map(alias => this.filterForm.facets.controls[alias]);
    }

    public getAggregationByControl(control: AbstractControl): AggregationGroupModel {
        let aggregation = null;
        Object.keys(this.filterForm.facets.controls).forEach((alias) => {
           if(this.filterForm.facets.controls[alias] === control) {
               aggregation = this.searchResult.filter.facets.find(x => x.alias === alias) || null;
           }
        });
        return aggregation;
    }

    public clearFilter(): void {
        this.filterForm.reset();
        this.filterForm.submit();
    }

    private setSearchMode(): void {
        if (this.filter.collectionGroup === ProductCollectionGroupsEnum.All) {
            this.searchMode = ProductsSearchModeEnum.AllCollections;
        } else {
            if (this.filter.collection === ProductCollectionsEnum.All) {
                this.searchMode = ProductsSearchModeEnum.CollectionGroup;
            } else {
                this.searchMode = ProductsSearchModeEnum.Collection;
            }
        }
    }
}
